import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PersonModel from 'tio-data/models/person';
import type PslfProfileModel from 'tio-data/models/pslf-profile';
import type PslfFormModel from 'tio-data/models/pslf-form';
import type PslfGroup from 'tio-data/models/pslf-group';
import type RecommendationModel from 'tio-data/models/recommendation';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type FeaturesService from 'tio-common/services/features';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import { getLoanSummaryRequest } from 'tio-common/requests/user';

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare pslfDebug: PslfDebugService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const pslfProfile = parentModel.pslfProfile;

    const [loanSummaryResp, person, mostRecentRecQuery, pslfForms] = await Promise.all([
      userId
        ? this.store.request(getLoanSummaryRequest(userId))
        : Promise.resolve({ content: undefined as unknown }),
      // @ts-expect-error type models
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans',
        reload: true,
      }),
      this.store.query<RecommendationModel>('recommendation', {
        filter: {
          user: userId,
          'requesting-app': 'PSLF',
        },
        include: 'user',
        sort: '-created-at',
        page: {
          offset: 0,
          limit: 1,
        },
      }),
      this.store.query<PslfFormModel>('pslf-form', {
        filter: { 'pslf-profile': pslfProfile.id },
        include: 'attachments',
      }),
    ]);

    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummaryResp.content as LoanSummary,
      mostRecentRecommendation: mostRecentRecQuery?.[0] as RecommendationModel | undefined,
      person: person,
      pslfProfile: pslfProfile,
      pslfForms: pslfForms.slice() as PslfFormModel[],
      debugLinkLoans: false,
    };

    // Apply PSLF debug overrides (feature-flagged; dead code in production)
    if (this.features.isEnabled('pslfDebugPanel') && this.pslfDebug.isActive()) {
      return this.pslfDebug.applyTo(baseModel);
    }

    return baseModel;
  }
}
